import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import AccountContext from '../../../services/AccountContext';
import CollectionsContext from '../../../services/CollectionsContext';
import childrenService from '../../../services/children';

import styles from './WorksheetActions.module.less';
import Action from './Action';

const AssignmentsModal = dynamic(() => import('../../AssignmentsModal'), {
  ssr: false,
});

const GradeWorksheetModal = dynamic(() => import('../GradeWorksheetModal'), {
  ssr: false,
});

const SaveToDrive = dynamic(() => import('./SaveToDrive'), { ssr: false });

import { openFeatureWall } from '../../../services/payWallModal';
import clsx from 'clsx';

function WorksheetActions({
  id,
  thumbnail,
  title,
  lessonPlan,
  slideshow,
  answersheetHref,
  trackDownload,
  downloadHref,
  isInteractive = false,
}) {
  const [gradeWorksheetIsOpen, setGradeWorksheetIsOpen] = useState(false);
  const [assignmentsIsOpen, setAssignmentsIsOpen] = useState(false);

  const { isPremium, ready, isSchoolAdmin } = useContext(AccountContext);
  const { isCollected, collectContent } = useContext(CollectionsContext);

  //TODO use player/kids context instead of  this once it's merged
  const [numChildren, setNumChildren] = useState(0);
  useEffect(() => {
    childrenService.fetch().then(res => {
      setNumChildren(res.children.length);
    });
  }, []);

  const handleClickCollect = () => {
    collectContent('worksheet', id, {
      type: 'worksheet',
      id,
      thumbnail,
      title,
      isInteractive,
    });
  };

  const handleAnswersClick = e => {
    if (isPremium) {
      window.open(answersheetHref, '_blank', 'noopener');
      trackDownload && trackDownload({ answerKey: true });
      return;
    } else if (!ready) {
      return;
    }

    let walltext =
      '<p>Answer Sheets are available to Premium members only. </p><p>Upgrade to Premium membership to access answer sheets and over 20,000 worksheets.</p>';

    if (isSchoolAdmin && !isPremium) {
      walltext =
        '<p>Answer Sheets are available to Premium members only. To get access to Premium features, you must be added to the teachers list on your school account.</p>';
    }

    e.preventDefault();
    showFeatureWall(
      10.575,
      'Unlock Answer Sheets',
      walltext,
      'Answer Key Download'
    );
  };

  const showFeatureWall = (featureWallTitle, text, feature) => {
    openFeatureWall({
      contentType: 'worksheet',
      contentId: id,
      title: featureWallTitle,
      text,
      name: title,
      feature,
      section: 'Worksheet Detail',
    });
  };

  const gradeWorksheetEnabled = !isInteractive && isPremium && numChildren > 0;
  const inCollection = isCollected('worksheet', id);

  return (
    <div>
      <div className={styles.actionsContainer}>
        {lessonPlan && (
          <Action
            text="See in a Lesson Plan"
            icon="set"
            href={`/lesson-plan/${lessonPlan}/`}
            target="_blank"
            isImg={false}
          />
        )}
        {slideshow ? (
          <Action
            text={`See in a set (${slideshow.count})`}
            icon="set"
            href={`/slideshow/${slideshow.url}/`}
            title={`Set: ${slideshow.title}`}
            target="_blank"
            rel="noopener noreferrer"
            isImg={false}
          />
        ) : (
          ''
        )}
        {answersheetHref && (
          <Action
            icon="check"
            text="View answer key"
            onClick={handleAnswersClick}
            target="_blank"
            rel="noopener noreferrer"
            isImg={false}
          />
        )}
        <Action
          icon={inCollection ? 'bookmark-filled' : 'bookmark'}
          text={inCollection ? 'Added to collection' : 'Add to collection'}
          onClick={handleClickCollect}
          isImg={false}
        />
        <Action
          icon="child-face"
          text="Add to assignment"
          onClick={() => setAssignmentsIsOpen(true)}
          isImg={false}
        />
        {gradeWorksheetEnabled && (
          <Action
            icon="pencil"
            text="Grade worksheet"
            onClick={() => {
              setGradeWorksheetIsOpen(true);
            }}
            isImg={false}
          />
        )}
        <SaveToDrive
          className={clsx(styles.saveToDrive)}
          name={title}
          contentType="worksheet"
          contentTypeDisplayName="Worksheet"
          downloadHref={downloadHref}
        />
      </div>
      {gradeWorksheetIsOpen ? (
        <GradeWorksheetModal
          contentid={id}
          open={gradeWorksheetIsOpen}
          setOpen={setGradeWorksheetIsOpen}
        />
      ) : null}
      <AssignmentsModal
        onClose={() => setAssignmentsIsOpen(false)}
        isOpen={assignmentsIsOpen}
        itemsToAdd={[
          {
            type: isInteractive ? 'fillable-worksheet' : 'worksheet',
            internalName: id,
          },
        ]}
        contentId={id}
        section="Worksheet Detail"
        contentType={'worksheet'}
      />
    </div>
  );
}

WorksheetActions.propTypes = {
  title: PropTypes.string,
  thumbnail: PropTypes.string,
  id: PropTypes.string,
  lessonPlan: PropTypes.string,
  slideshow: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    count: PropTypes.number,
  }),
  trackDownload: PropTypes.func,
  answersheetHref: PropTypes.string,
  downloadHref: PropTypes.string,
  isInteractive: PropTypes.bool,
};

export default WorksheetActions;
